<template>
  <div>
    <div class="row">
      <div class="col-sm-9">
        <h4 class="text-left font-weight-bold">RIGHTSHIP QUESTIONNAIRE SETTINGS</h4>
      </div>
      <div class="col-sm-3 text-right">
        <button class="e-btn e-btn-green e-text-white pr-3 mt-2 float-right" @click="openCreateModal">
          <font-awesome-icon icon="plus-square" style="width: 25px" />
          <small class="font-weight-bold">CREATE QUESTIONNAIRE</small>
        </button>
      </div>
    </div>

    <div class="row align-items-center mt-3">
      <div class="col-sm-6">
        <nav>
          <ul class="pagination text-smallest mb-2 pb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="currentPage = 1">First</button>
            </li>
            <li
              class="page-item"
              v-for="page in visiblePages"
              :key="page"
              :class="{ active: page === currentPage }"
            >
              <button class="page-link" @click="currentPage = page">{{ page }}</button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <button class="page-link" @click="currentPage = totalPages">Last</button>
            </li>
          </ul>
        </nav>
      </div>

      <div class="col-sm-6 d-flex justify-content-end align-items-center">
        <select
          v-model="selectedRevisionId"
          @change="currentPage = 1"
          class="form-control form-control-sm mr-2"
          style="max-width: 200px; font-weight: bold;"
        >
          <option
            v-if="selectedRevisionId && selectedRevision"
            :value="selectedRevisionId"
            disabled
          >
            v{{ selectedRevision.revision_number }}
          </option>

          <option
            v-for="rev in revisionNumbers"
            :key="rev.id"
            :value="rev.id"
            :disabled="rev.id === selectedRevisionId"
          >
            v{{ rev.revision_number }}
          </option>
        </select>
        <form autocomplete="off" @submit.prevent>
          <input
            v-model="searchQuery"
            type="text"
            class="form-control form-control-sm"
            placeholder="Search Questionnaire"
            style="max-width: 300px;"
          />
        </form>
      </div>
    </div>

    <div class="row mt-3">
      <table class="table table-sm table-bordered small">
        <thead>
        <tr>
          <th scope="col" style="width: 40px;">#</th>
          <th scope="col">CODE</th>
          <th scope="col">QUESTION</th>
          <th scope="col">ACTION</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="loading">
          <td colspan="3"><loading /></td>
        </tr>
        <tr v-else v-for="(questionnaire, index) in paginatedQuestionnaires" :key="questionnaire.id">
          <td>{{ (currentPage - 1) * perPage + index + 1 }}</td>
          <td style="width: 150px;">{{ questionnaire.code }}</td>
          <td v-html="questionnaire.main_question"></td>
          <td style="width: 100px;">
            <div class="btn-group dropleft">
              <button class="mr-2 btn btn-sm btn-secondary force-white-all" data-toggle="dropdown">
                <font-awesome-icon icon="cogs" />
              </button>
              <div class="dropdown-menu">
                <button class="dropdown-item text-secondary font-weight-bolder" @click="openUpdateModal(questionnaire)">
                  <font-awesome-icon icon="pen" /> Update
                </button>
                <button class="dropdown-item text-danger font-weight-bolder" @click="deleteQuestionnaire(questionnaire.id)">
                  <font-awesome-icon icon="trash" class="force-danger-all" /> Delete
                </button>
              </div>
            </div>
          </td>
        </tr>
        <tr v-if="paginatedQuestionnaires.length === 0">
          <td class="text-center font-weight-bold text-muted" colspan="5">
            NO DATA SAVED
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <BaseModal :is-visible="showCreateModal" size="xl" :close-button="false">
      <template v-slot:title><h4 class="font-weight-bold">CREATE QUESTIONNAIRE</h4></template>
      <template v-slot:body>
        <form @submit.prevent="submitCreate">
          <div class="form-group row">
            <label class="col-sm-3 col-form-label">Revision Number</label>
            <div class="col-sm-9">
              <select v-model="form.revision_number_id" @change="clearCreateErrors('revision_number_id')" class="form-control"
                      :class="{ 'is-invalid': createErrors.revision_number_id }"
              >
                <option disabled value="">-- Select Revision Number --</option>

                <option
                  v-if="form.revision_number_id && selectedCreateRevision"
                  :value="form.revision_number_id"
                  disabled
                >
                  v{{ selectedCreateRevision.revision_number }}
                </option>

                <option
                  v-for="rev in revisionNumbers"
                  :key="rev.id"
                  :value="rev.id"
                  v-show="rev.id !== form.revision_number_id"
                >
                  v{{ rev.revision_number }}
                </option>
              </select>
              <small v-if="createErrors.revision_number_id" class="text-danger font-weight-bold">
                {{ createErrors.revision_number_id[0] }}
              </small>
            </div>
          </div>

          <div class="form-group row">
            <label class="col-sm-3 col-form-label">Parent Question</label>
            <div class="col-sm-9">
              <select v-model="form.parent_id" class="form-control">
                <option disabled :value="null">-- Select Parent Question --</option>
                <option
                  v-for="q in createParentCandidates"
                  :key="q.id"
                  :value="q.id"
                >
                  {{ q.code }} - {{ q.main_question.replace(/<[^>]*>/g, '').slice(0, 50) }}
                </option>
              </select>
            </div>
          </div>

          <div class="form-group row">
            <label class="col-sm-3 col-form-label">
              Code
              <span v-if="!form.code || (form.code && !codeFormatValid)" class="text-danger font-weight-bold h6">*</span>
            </label>
            <div class="col-sm-9">
              <input
                @input="clearCreateErrors('code')"
                type="text"
                v-model="form.code"
                class="form-control"
                :class="{
    'is-invalid': (!form.code || !codeFormatValid || createErrors.code)
  }"
                placeholder="e.g., 1.0 / 11.11 / 1.1.1"
              />
              <small v-if="createErrors.code" class="text-danger font-weight-bold">
                {{ createErrors.code[0] }}
              </small>
              <small
                v-if="!form.code"
                class="text-danger font-weight-bold"
              >
                Code is required.
              </small>
              <small
                v-else-if="!codeFormatValid"
                class="text-danger font-weight-bold"
              >
                Code must be in format (e.g., 1.0 / 11.11 / 1.1.1)
              </small>
            </div>
          </div>

          <div class="form-group row">
            <label class="col-sm-3 col-form-label">
              Question
              <span v-if="!form.main_question" class="text-danger font-weight-bold h6">*</span>
            </label>
            <div class="col-sm-9">
    <textarea
      class="form-control my-2"
      @input="clearCreateErrors('main_question')"
      v-model="form.main_question"
      :class="{
    'is-invalid': !form.main_question || createErrors.main_question
  }"
      rows="5"
    />
              <small v-if="createErrors.main_question" class="text-danger font-weight-bold">
                {{ createErrors.main_question[0] }}
              </small>
              <small
                v-if="!form.main_question"
                class="text-danger font-weight-bold"
              >
                Question is required.
              </small>

            </div>
          </div>
        </form>
      </template>
      <template v-slot:footer>
        <button class="btn btn-sm btn-secondary" @click="onClickCloseCreateModal">Cancel</button>
        <button
          class="btn btn-sm btn-primary"
          :disabled="isCreateFormIncomplete"
          @click="submitCreate"
        >
          Create
        </button>
      </template>
    </BaseModal>

    <BaseModal :is-visible="showUnsavedCreateConfirmModal" @close="cancelCloseCreateModal">
      <template v-slot:title>
        <h4 class="font-weight-bold">UNSAVED CHANGES</h4>
      </template>
      <template v-slot:body>
        <h6 class="text-center font-weight-bold">
          Are you sure you want to close?<br/>
          <span class="text-danger">You have unsaved changes.</span>
        </h6>
      </template>
      <template v-slot:footer>
        <div class="d-flex justify-content-center w-100">
          <button class="btn btn-sm btn-warning font-weight-bold" @click="confirmCloseCreateModal">CLOSE</button>
        </div>
      </template>
    </BaseModal>

    <BaseModal :is-visible="showUpdateModal" size="xl" :close-button="false">
    <template v-slot:title><h4 class="font-weight-bold">UPDATE QUESTIONNAIRE</h4></template>
      <template v-slot:body>
        <form @submit.prevent="submitUpdate">
          <div class="form-group row">
            <label class="col-sm-3 col-form-label">Revision Number</label>
            <div class="col-sm-9">
              <select
                @change="clearUpdateErrors('revision_number_id')"
                v-model="form.revision_number_id"
                class="form-control"
                :class="{ 'is-invalid': updateErrors.revision_number_id }"
              >
                <option disabled value="">-- Select Revision Number --</option>
                <option
                  v-if="form.revision_number_id && selectedUpdateRevision"
                  :value="form.revision_number_id"
                  disabled
                >
                  v{{ selectedUpdateRevision.revision_number }}
                </option>
                <option
                  v-for="rev in revisionNumbers"
                  :key="rev.id"
                  :value="rev.id"
                  v-show="rev.id !== form.revision_number_id"
                >
                  v{{ rev.revision_number }}
                </option>
              </select>
              <small v-if="updateErrors.revision_number_id" class="text-danger font-weight-bold">
                {{ updateErrors.revision_number_id[0] }}
              </small>
            </div>
          </div>

          <div class="form-group row">
            <label class="col-sm-3 col-form-label">Parent Question</label>
            <div class="col-sm-9">
              <select v-model="form.parent_id" class="form-control">
                <option disabled :value="null">-- Select Parent Question --</option>
                <option
                  v-for="q in parentCandidates"
                  :key="q.id"
                  :value="q.id"
                >
                  {{ q.code }} - {{ q.main_question.replace(/<[^>]*>/g, '').slice(0, 50) }}
                </option>
              </select>
            </div>
          </div>

          <div class="form-group row">
            <label class="col-sm-3 col-form-label">
              Code
              <span v-if="!form.code || (form.code && !codeFormatValid)" class="text-danger font-weight-bold h6">*</span>
            </label>
            <div class="col-sm-9">
              <input
                @input="clearUpdateErrors('code')"
                type="text"
                v-model="form.code"
                class="form-control"
                :class="{
    'is-invalid': (!form.code || !codeFormatValid || updateErrors.code)
  }"
                placeholder="e.g., 1.0 / 11.11 / 1.1.1"
              />
              <small v-if="updateErrors.code" class="text-danger font-weight-bold">
                {{ updateErrors.code[0] }}
              </small>
              <small v-if="!form.code" class="text-danger font-weight-bold">
                Code is required.
              </small>
              <small v-else-if="!codeFormatValid" class="text-danger font-weight-bold">
                Code must be in format (e.g., 1.0 / 11.11 / 1.1.1)
              </small>
              <small v-if="!form.code" class="text-danger font-weight-bold">
                Code is required.
              </small>
            </div>
          </div>

          <div class="form-group row">
            <label class="col-sm-3 col-form-label">
              Question
              <span v-if="!form.main_question" class="text-danger font-weight-bold h6">*</span>
            </label>
            <div class="col-sm-9">
    <textarea
      @input="clearUpdateErrors('main_question')"
      class="form-control my-2"
      v-model="form.main_question"
      :class="{ 'is-invalid': !form.main_question || updateErrors.main_question }"
      rows="5"
    ></textarea>
              <small v-if="updateErrors.main_question" class="text-danger font-weight-bold">
                {{ updateErrors.main_question[0] }}
              </small>
              <small v-if="!form.main_question" class="text-danger font-weight-bold">
                Question is required.
              </small>
            </div>
          </div>
        </form>
      </template>
      <template v-slot:footer>
        <button class="btn btn-sm btn-secondary" @click="onClickCloseUpdateModal">Cancel</button>
        <button
          class="btn btn-sm btn-primary"
          :disabled="isUpdateFormIncomplete"
          @click="submitUpdate"
        >
          Update
        </button>
      </template>
    </BaseModal>

    <BaseModal :is-visible="showUnsavedConfirmModal" @close="cancelCloseUpdateModal">
      <template v-slot:title>
        <h4 class="font-weight-bold">UNSAVED CHANGES</h4>
      </template>
      <template v-slot:body>
        <h6 class="text-center font-weight-bold">Are you sure you want to close?<br/>
          <span class="text-danger">You have unsaved changes.</span>
        </h6>
      </template>
      <template v-slot:footer>
        <div class="d-flex justify-content-center w-100">
          <button class="btn btn-sm btn-warning font-weight-bold" @click="confirmCloseUpdateModal">CLOSE</button>
        </div>
      </template>
    </BaseModal>

    <BaseModal :is-visible="showDeleteModal" @close="showDeleteModal = false">
      <template v-slot:title>
        <h4 class="font-weight-bold">CONFIRM DELETION</h4>
      </template>
      <template v-slot:body>
        <p class="h6 text-center font-weight-bold mb-3">
          Are you sure you want to delete this questionnaire?<br />
          <span class="text-danger">This action cannot be undone.</span>
        </p>
        <div class="form-group">
          <label class="font-weight-bold">Enter your password to confirm:</label>
          <input
            @input="clearDeleteError"
            type="password"
            class="form-control form-control-sm"
            v-model="deletePassword"
            placeholder="Password"
          />
          <small v-if="deletePasswordError" class="text-danger font-weight-bold d-block mt-2">
            {{ deletePasswordError }}
          </small>
        </div>
      </template>
      <template v-slot:footer>
        <div class="d-flex justify-content-center w-100">
          <button
            class="btn btn-sm btn-danger font-weight-bold"
            @click="confirmDelete"
            :disabled="!deletePassword.trim()"
          >
            DELETE
          </button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { QuestionnaireService } from '@/services/QuestionnaireService';
import { RevisionService } from '@/services/RevisionService';
import BaseModal from '@/components/common/BaseModal.vue';
import loading from '@/components/elements/AppLoading.vue';
import { AlertService } from '@/services/AlertService';

export default {
  selectedCreateRevision() {
    return this.revisionNumbers.find(rev => rev.id === this.form.revision_number_id) || null;
  },
  selectedUpdateRevision() {
    return this.revisionNumbers.find(rev => rev.id === this.form.revision_number_id) || null;
  },
  components: {
    BaseModal,
    loading,
  },
  data() {
    return {
      deletePassword: '',
      deletePasswordError: '',
      createErrors: {},
      updateErrors: {},
      showUnsavedCreateConfirmModal: false,
      showUnsavedConfirmModal: false,
      closeUpdateModalAfterConfirm: false,
      questionnaires: [],
      revisionNumbers: [],
      selectedRevisionId: '',
      searchQuery: '',
      currentPage: 1,
      perPage: 20,
      loading: false,
      showCreateModal: false,
      showUpdateModal: false,
      showDeleteModal: false,
      deleteTargetId: null,
      pristineForm: null,
      form: {
        id: null,
        code: '',
        main_question: '',
        revision_number_id: '',
        parent_id: null,
      },
      toolbar: [
        ['bold', 'italic', 'strike', 'underline'],
        [{
          color: [
            "#000000", "#e60000", "#ff9900", "#ffff00", "#008a00", "#0066cc", "#9933ff",
            "#ffffff", "#facccc", "#ffebcc", "#ffffcc", "#cce8cc", "#cce0f5", "#ebd6ff",
            "#bbbbbb", "#f06666", "#ffc266", "#ffff66", "#66b966", "#66a3e0", "#c285ff",
            "#888888", "#a10000", "#b26b00", "#b2b200", "#006100", "#0047b2", "#6b24b2",
            "#444444", "#5c0000", "#663d00", "#666600", "#003700", "#002966", "#3d1466"
          ]
        }]
      ]
    };
  },
  computed: {
    isUpdateFormIncomplete() {
      return (
        !this.form.code ||
        !this.codeFormatValid ||
        !this.form.main_question
      );
    },
    codeFormatValid() {
      // return /^\d{1,3}\.\d{1,3}$/.test(this.form.code.trim());
      return /^\d{1,3}\.\d{1,3}(\.\d{1,3})?$/.test(this.form.code.trim());
    },
    isCreateFormIncomplete() {
      return (
        !this.form.code ||
        !this.form.main_question ||
        !this.form.revision_number_id ||
        !this.codeFormatValid
      );
    },
    hasUnsavedCreateChanges() {
      const f = this.form;
      const p = this.pristineForm;
      if (!p) return false;

      return (
        f.code !== p.code ||
        f.main_question !== p.main_question ||
        f.revision_number_id !== p.revision_number_id ||
        f.parent_id !== p.parent_id
      );
    },
    hasUnsavedChanges() {
      const f = this.form;
      const p = this.pristineForm;
      if (!p) return false;
      return (
        f.code !== p.code ||
        f.main_question !== p.main_question ||
        f.revision_number_id !== p.revision_number_id ||
        f.parent_id !== p.parent_id
      );
    },
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 2;
      const range = [];
      const start = Math.max(1, current - delta);
      const end = Math.min(total, current + delta);
      for (let i = start; i <= end; i++) {
        range.push(i);
      }
      return range;
    },
    filteredQuestionnaires() {
      if (!this.selectedRevisionId) return [];
      const search = this.searchQuery.trim().toLowerCase();
      return [...this.questionnaires]
        .filter(q =>
          q.revision_number_id === this.selectedRevisionId &&
          q.parent_id !== null
        )
        .filter(q =>
          q.code.toLowerCase().includes(search) ||
          q.main_question.toLowerCase().includes(search)
        )
        .sort((a, b) => {
          // const aParts = a.code.split('.').map(Number);
          // const bParts = b.code.split('.').map(Number);
          // for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
          //   const aNum = aParts[i] || 0;
          //   const bNum = bParts[i] || 0;
          //   if (aNum !== bNum) return aNum - bNum;
          // }
          // return 0;

          const codeCompare = a.code.localeCompare(b.code, 'en', {
            numeric: true,
            sensitivity: 'base'
          });

          if (codeCompare !== 0) return codeCompare;

          const cleanA = a.main_question.replace(/^[\d\s.-]+/, '').trim();
          const cleanB = b.main_question.replace(/^[\d\s.-]+/, '').trim();

          return cleanA.localeCompare(cleanB);
        });
    },
    totalPages() {
      return Math.ceil(this.filteredQuestionnaires.length / this.perPage);
    },
    paginatedQuestionnaires() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredQuestionnaires.slice(start, start + this.perPage);
    },
    parentCandidates() {
      return this.questionnaires
        .filter(q =>
          q.id !== this.form.id &&
          (
            q.revision_number_id === 0 ||
            q.parent_id === null
          )
        )
        .sort((a, b) => a.code.localeCompare(b.code));
    },
    createParentCandidates() {
      return this.questionnaires
        .filter(q =>
          (
            q.revision_number_id === 0 ||
            q.parent_id === null
          )
        )
        .sort((a, b) => a.code.localeCompare(b.code));
    }
  },
  async created() {
    await this.fetchRevisionNumbers();
    await this.fetchQuestionnaires();
    if (this.revisionNumbers.length > 0) {
      this.selectedRevisionId = this.revisionNumbers[0].id;
    }
  },
  methods: {
    clearDeleteError() {
      this.deletePasswordError = '';
    },
    clearCreateErrors(field = null) {
      if (field) {
        this.$delete(this.createErrors, field);
      } else {
        this.createErrors = {};
      }
    },
    clearUpdateErrors(field = null) {
      if (field) {
        this.$delete(this.updateErrors, field);
      } else {
        this.updateErrors = {};
      }
    },
    onClickCloseCreateModal() {
      this.closeCreateModal();
    },
    onClickCloseUpdateModal() {
      this.closeUpdateModal();
    },
    async fetchQuestionnaires() {
      this.loading = true;
      try {
        const response = await QuestionnaireService.getAll();
        this.questionnaires = response.data;
      } catch (error) {
        console.error('Failed to load questionnaires', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchRevisionNumbers() {
      try {
        const response = await RevisionService.getAll();
        this.revisionNumbers = response.data;
      } catch (error) {
        console.error('Failed to load revision numbers', error);
      }
    },
    openCreateModal() {
      this.resetForm();
      this.form.revision_number_id = this.selectedRevisionId;

      const candidates = this.createParentCandidates;
      if (candidates.length > 0) {
        this.form.parent_id = candidates[0].id;
      }
      this.pristineForm = { ...this.form };

      this.showCreateModal = true;
    },
    closeCreateModal(force = false) {
      if (this.hasUnsavedCreateChanges && !force) {
        this.showCreateModal = false;
        this.showUnsavedCreateConfirmModal = true;
        return;
      }

      this.resetForm();
      this.showCreateModal = false;
    },
    confirmCloseCreateModal() {
      this.showUnsavedCreateConfirmModal = false;
      this.closeCreateModal(true);
    },
    cancelCloseCreateModal() {
      this.showUnsavedCreateConfirmModal = false;
      this.showCreateModal = true;
    },
    async submitCreate() {
      if (this.isCreateFormIncomplete) return;

      try {
        await QuestionnaireService.create({
          code: this.form.code,
          main_question: this.form.main_question,
          revision_number_id: this.form.revision_number_id,
          parent_id: this.form.parent_id,
        });
        await this.fetchQuestionnaires();
        this.resetForm();
        this.showCreateModal = false;
        this.showUnsavedCreateConfirmModal = false;

        AlertService.successAlert('Questionnaire created successfully', 'CREATE');
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.createErrors = error.response.data.errors || {};
        } else {
          AlertService.errorAlert('Creation failed', 'ERROR');
        }
      }
    },
    openUpdateModal(q) {
      this.form = {
        id: q.id,
        code: q.code,
        main_question: q.main_question,
        revision_number_id: q.revision_number_id,
        parent_id: q.parent_id || null,
      };
      this.pristineForm = { ...this.form };
      this.showUpdateModal = true;
    },
    closeUpdateModal(force = false) {
      if (this.hasUnsavedChanges && !force) {
        this.showUpdateModal = false;
        this.showUnsavedConfirmModal = true;
        return;
      }
      this.resetForm();
      this.showUpdateModal = false;
    },
    confirmCloseUpdateModal() {
      this.showUnsavedConfirmModal = false;
      this.closeUpdateModal(true);
    },
    cancelCloseUpdateModal() {
      this.showUnsavedConfirmModal = false;
      this.showUpdateModal = true;
    },
    async submitUpdate() {
      if (this.isUpdateFormIncomplete) return;
      try {
        await QuestionnaireService.update(this.form.id, {
          code: this.form.code,
          main_question: this.form.main_question,
          revision_number_id: this.form.revision_number_id,
          parent_id: this.form.parent_id,
        });
        await this.fetchQuestionnaires();
        this.showUpdateModal = false;
        this.showUnsavedConfirmModal = false;
        this.resetForm();
        AlertService.successAlert('Successfully updated questionnaire', 'UPDATE');
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.updateErrors = error.response.data.errors || {};
        } else {
          AlertService.errorAlert('Update failed', 'ERROR');
        }
      }
    },
    deleteQuestionnaire(id) {
      this.deleteTargetId = id;
      this.deletePassword = '';
      this.deletePasswordError = '';
      this.showDeleteModal = true;
    },
    async confirmDelete() {
      try {
        await QuestionnaireService.deleteWithPassword(this.deleteTargetId, this.deletePassword);
        await this.fetchQuestionnaires();
        this.showDeleteModal = false;
        this.deletePassword = '';
        AlertService.successAlert('Successfully deleted questionnaire', 'DELETE');
      } catch (error) {
        if (error.response && error.response.status === 403) {
          this.deletePasswordError = 'Incorrect password. Please try again.';
        } else {
          AlertService.errorAlert('Deletion failed', 'ERROR');
        }
      }
    },
    resetForm() {
      this.createErrors = {};
      this.form = {
        id: null,
        code: '',
        main_question: '',
        revision_number_id: '',
        parent_id: null,
      };
    }
  }
};
</script>
<style scoped>
th, td {
  text-align: center;
  vertical-align: middle;
}
</style>
